import { Space, Tag } from 'antd';
import { useCallback, useEffect } from 'react';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import {
  Markup,
  MarkupTypeEnum,
  SkuMarkupTableRecord,
} from 'domains/markupDomain';
import { RevxButton } from 'components/RevxButton';

const toSkuMarkupTableRecord = (m: Markup) => {
  return {
    ...m,
    orgCount: m.orgSkuFilters?.length || 0,
    skuCount:
      m.orgSkuFilters?.reduce((prev, curr) => ({
        orgId: 0,
        skuIds: [...prev.skuIds, ...curr.skuIds],
      })).skuIds.length || 0,
    tiers: m.markupRanges?.length || 0,
  } as SkuMarkupTableRecord;
};

const SkuMarkupTable = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const isRevshop = useAppSelector(
    (state) => state.markup.contextMode === 'revshop'
  );

  // table data processing
  const data = useAppSelector((state) => {
    if (isRevshop && state.markup.revshopSkuMarkups?.content) {
      return state.markup.revshopSkuMarkups.content.map(toSkuMarkupTableRecord);
    } else if (!isRevshop && state.markup.skuMarkups?.content) {
      return state.markup.skuMarkups.content.map(toSkuMarkupTableRecord);
    } else {
      return [];
    }
  });
  const total = useAppSelector(
    isRevshop
      ? (state) => state.markup.revshopSkuMarkups?.totalElements || 0
      : (state) => state.markup.skuMarkups?.totalElements || 0
  );
  const currentPage = useAppSelector((state) =>
    isRevshop ? state.markup.revshopSkuCurrentPage : state.markup.skuCurrentPage
  );
  const pageSize = useAppSelector((state) =>
    isRevshop ? state.markup.revshopSkuPageSize : state.markup.skuPageSize
  );
  const loading = useAppSelector(
    (state) =>
      state.loading.effects.markup.getSkuMarkups ||
      state.loading.effects.markup.getRevshopSkuMarkups
  );

  useEffect(() => {
    if (isRevshop) {
      dispatch.markup.getRevshopSkuMarkups({
        page: { currentPage, pageSize },
        query: { type: MarkupTypeEnum.REVSHOP_SKU },
      });
    } else {
      dispatch.markup.getSkuMarkups({
        page: { currentPage, pageSize },
        query: { type: MarkupTypeEnum.ORG_SKU },
      });
    }
  }, [currentPage, pageSize, dispatch.markup, isRevshop]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const currentPage = pagination.current;
          const pageSize = pagination.pageSize;
          dispatch.markup.setState(
            isRevshop
              ? {
                  revshopSkuCurrentPage: currentPage,
                  revshopSkuPageSize: pageSize,
                }
              : {
                  skuCurrentPage: currentPage,
                  skuPageSize: pageSize,
                }
          );
          break;
      }
    },
    [dispatch.markup, isRevshop]
  );
  const columns: TableColumnsType<SkuMarkupTableRecord> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      fixed: 'left',
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      width: 200,
    },
    {
      title: t('common.type'),
      dataIndex: 'isRange',
      render: (isRange) =>
        isRange
          ? t('platform.markup.labels.priceRange')
          : t('platform.markup.labels.flatMarkup'),
    },
    {
      title: t('platform.markup.columns.orgCount'),
      dataIndex: 'orgCount',
    },
    {
      title: t('platform.markup.columns.skuCount'),
      dataIndex: 'skuCount',
    },
    {
      title: t('platform.markup.columns.tiers'),
      dataIndex: 'tiers',
    },

    {
      title: t('common.createdAt'),
      dataIndex: 'createAt',
      valueType: 'time',
    },
    {
      title: t('common.active'),
      fixed: 'right',
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            onClick={() =>
              dispatch.markup.setState({
                curSkuMarkup: {
                  ...record,
                },
              })
            }
          >
            {t('common.view')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  return (
    <RevxTable<SkuMarkupTableRecord>
      loading={loading}
      columns={columns}
      dataSource={data}
      pagination={{
        pageSize,
        total,
      }}
      onChange={onTableChange}
    />
  );
};

export default SkuMarkupTable;
